import React, { useState } from "react";
import { Grid } from "semantic-ui-react";
import { getDropdownOptions, guid, handleAddressValue, handleInputType } from "../../../Helpers";
import { getIsWhiteLabeled } from "../../../helpers/PermissionHelper";
import { BrokerConstants } from "../../constants/BrokerConstants";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import BCFormInput from "../../ui/BCFormInput";
import BCTable from "../../ui/BCTable";
import BCValidationMessage from "../../ui/BCValidationMessage";
import BPLoanDetailSection from "./BPLoanDetailSection";

interface IBPLoanALReoTablesProps {
  accordionProps: any;
  applicationMetadata: any;
  brokerColor: any;
  invalidProps: any;
  isEditMode: any;
  assetTitle: any;
  assetAccordionId: any;
  liabilitiesTitle: any;
  liabilitiesAccordionId: any;
  reoTitle: any;
  reoAccordionId: any;
  isOrganization: any;
  organizationContact: any;
  mainPatchObj: any;
  setMainPatchObj: any;
  handleALDeleteAsync: any;
  handleREODeleteAsync: any;
}

const BPLoanALReoTables = ({...props}: IBPLoanALReoTablesProps) => {
  const [alSchedulePatchArray, setAlSchedulePatchArray] = useState([] as any);
  const [reoSchedulePatchArray, setReoSchedulePatchArray] = useState([] as any);

  const ReoValidationMessageBundle = () => {
    return (
      <React.Fragment>
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-reotypeId" propText="Please select a REO type." />
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-reostatusId" propText="Please select a status field." />
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-marketValue" propText="Please make sure market value field is 0 or above." />
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-street" propText="Street name is missing." />
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-cityName" propText="City name is missing." />
        <BCValidationMessage invalidProps={props.invalidProps} prop="reo-stateName" propText="State is missing." />
      </React.Fragment>
    );
  };

  const handleALChange = (obj, value, field, inputType = "string", charLimit : number | null = null) => {
    const inputValue = charLimit ? handleInputType(inputType, value, undefined, undefined, charLimit) : value;
    obj[field] = inputValue;
    const editALSchedule = alSchedulePatchArray.find(x => x.id === obj.id);
    if (editALSchedule) {
      editALSchedule[field] = inputValue;
    }
    else {
      const newALSchedule = {id: obj.id};
      newALSchedule[field] = inputValue;
      alSchedulePatchArray.push({...newALSchedule});
    }

    props.setMainPatchObj({...props.mainPatchObj, "alSchedule": [...alSchedulePatchArray]});
  };

  const handleReoChange = (obj, value, field, inputType = "string", charLimit : number | null = null) => {
    const inputValue = charLimit ? handleInputType(inputType, value, undefined, undefined, charLimit) : value;
    obj[field] = inputValue;
    const editReoSchedule = reoSchedulePatchArray.find(x => x.id === obj.id);
    if (editReoSchedule) {
      editReoSchedule[field] = inputValue;
    }
    else {
      const newReoSchedule = {id: obj.id};
      newReoSchedule[field] = inputValue;
      reoSchedulePatchArray.push({...newReoSchedule});
    }

    props.setMainPatchObj({...props.mainPatchObj, "reoSchedule": [...reoSchedulePatchArray]});
  };

  const handleAddressChange = (placeDetailObject, obj) => {
    const newAddress = {
      "street": placeDetailObject.street,
      "cityName": placeDetailObject.cityName,
      "stateName": placeDetailObject.stateName,
      "countyName": placeDetailObject.countyName,
      "zipCode": placeDetailObject.zipCode,
    };

    const editReoSchedule = reoSchedulePatchArray.find(x => x.id === obj.id);
    if (editReoSchedule) {
      for (const [key, value] of Object.entries(newAddress)){
        editReoSchedule[key] = value;
      }
    }
    else {
      const newReoSchedule = {id: obj.id};
      for (const [key, value] of Object.entries(newAddress)){
        newReoSchedule[key] = value;
      }

      reoSchedulePatchArray.push({...newReoSchedule});
    }

    props.setMainPatchObj({...props.mainPatchObj, "reoSchedule": [...reoSchedulePatchArray]});
  };

  const handleALAdd = (alType) => {
    const alschedules = props.organizationContact.alschedules ? props.organizationContact.alschedules : [];
    const newALSchedule = {
      altypeId: null,
      alstatusId: "29186081-6ae1-434d-8b8a-ac8600b4b8f5",
      id: guid(),
      institution: null,
      value: 0
    };

    if (props.isOrganization) {
      newALSchedule["borrowerOrganizationId"] = props.organizationContact.id;
    }
    else {
      newALSchedule["organizationContactId"] = props.organizationContact.id;
    }

    if (alType === BrokerConstants.ALScheduleType.ASSET) {
      newALSchedule["alclassId"] = GlobalConstants.AlClassId.ASSET;
    }
    else {
      newALSchedule["alclassId"] = GlobalConstants.AlClassId.LIABILITY;
    }

    alschedules.push({...newALSchedule});
    alSchedulePatchArray.push({...newALSchedule});
    props.setMainPatchObj({...props.mainPatchObj, "alSchedule": [...alSchedulePatchArray]});
  };

  const handleREOAdd = () => {
    const reoschedules = props.organizationContact.reoschedules || [];
    const newReoschedules = {
      isNew: true,
      id: guid(),
      reostatusId: null,
      street: null,
      cityName: null,
      stateName: null,
      countyName: null,
      zipCode: null,
      marketValue: 0,
      ...(FeatureFlags.IS_BETA ? {
        reotypeId: "14b34581-1e60-4484-9624-a811010d8b88",
        mortgageHolder1: null,
        mortgageAmount1: 0,
        additionalCollateral: false,
      } : {
        reotypeId: null,
        mortgageFree: false,
        monthlyTaxInsuranceAndMaintenance: 0,
      })
    };

    if (props.isOrganization) {
      newReoschedules["borrowerOrganizationId"] = props.organizationContact.id;
    }
    else {
      newReoschedules["organizationContactId"] = props.organizationContact.id;
    }

    reoschedules.push({...newReoschedules});
    reoSchedulePatchArray.push({...newReoschedules});
    props.setMainPatchObj({...props.mainPatchObj, "reoSchedule": [...reoSchedulePatchArray]});
  };

  const handleALDelete = (aLId) => {
    props.organizationContact.alschedules = props.organizationContact.alschedules.filter(al => al.id !== aLId);
    props.handleALDeleteAsync(aLId);
  };

  const handleREODelete = (reoId) => {
    props.organizationContact.reoschedules = props.organizationContact.reoschedules.filter(reo => reo.id !== reoId);
    props.handleREODeleteAsync(reoId);
  };

  const handleDisplayValue = (field, oldObject, PatchArray) => {
    const newObject = PatchArray.find(x => x.id === oldObject.id);
    if (newObject && field in newObject && props.isEditMode) {
      return newObject[field];
    }

    return oldObject[field];
  };

  const formatAL = (type) => {
    const alschedules = props.organizationContact.alschedules;
    const formattedAssets = [] as any[];
    const formattedLiabilities = [] as any[];
    alschedules.forEach((alschedule) => {
      const alObj = {
        "id": alschedule.id,
        "Category": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            inputType="text"
            value={handleDisplayValue("alclassName", alschedule, alSchedulePatchArray)}
            field="alclassName"
            handleFormChange={(value, field) => handleALChange(alschedule, value, field)}
            disabled={true}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`al-${alschedule.id}-alclassName`} />
        </React.Fragment>,
        "Type": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="dropdown"
            placeholder="Please Select"
            dropdownOptions={alschedule.alclassId === GlobalConstants.AlClassId.LIABILITY ? getDropdownOptions(props.applicationMetadata.liabilityTypes, ["id"], "name") : getDropdownOptions(props.applicationMetadata.assetTypes, ["id"], "name")}
            value={handleDisplayValue("altypeId", alschedule, alSchedulePatchArray)}
            field="altypeId"
            handleFormChange={(value, field) => handleALChange(alschedule, value, field)}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`al-${alschedule.id}-altypeId`} />
        </React.Fragment>,
        "Institution": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="Construction Loan Services II"
            value={handleDisplayValue("institution", alschedule, alSchedulePatchArray)}
            field="institution"
            inputType={GlobalConstants.InputTypes.STRING}
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleALChange(alschedule, value, field, GlobalConstants.InputTypes.STRING, 50)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`al-${alschedule.id}-institution`} />
        </React.Fragment>,
        "Current balance": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="10000"
            fieldType={GlobalConstants.InputTypes.DOLLAR}
            inputType={GlobalConstants.InputTypes.DOLLAR}
            value={handleDisplayValue("value", alschedule, alSchedulePatchArray)}
            field="value"
            handleFormChange={(value, field) => handleALChange(alschedule, value, field, GlobalConstants.InputTypes.DOLLAR)}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`al-${alschedule.id}-value`} />
        </React.Fragment>
      };

      if (alschedule.alclassName === "Liability" || alschedule.alclassId === "5584023c-7712-400d-bdd9-a7fa006bb2be") {
        alObj["Monthly Liability"] = <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="10000"
            fieldType={GlobalConstants.InputTypes.DOLLAR}
            inputType={GlobalConstants.InputTypes.DOLLAR}
            value={handleDisplayValue("monthlyLiability", alschedule, alSchedulePatchArray)}
            field="monthlyLiability"
            handleFormChange={(value, field) => handleALChange(alschedule, value, field, GlobalConstants.InputTypes.DOLLAR)}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`al-${alschedule.id}-monthlyLiability`} />
        </React.Fragment>;
        formattedLiabilities.push(alObj);
      }
      else {
        formattedAssets.push(alObj);
      }
    });

    if (type === BrokerConstants.ALScheduleType.LIABILITY) {
      return formattedLiabilities;
    }
    else {
      return formattedAssets;
    }
  };

  const formatREO = () => {
    const reoschedules = props.organizationContact.reoschedules;
    const formattedREO = [] as any[];
    reoschedules.forEach((reoschedule) => {
      formattedREO.push({
        "id": reoschedule.id,
        "Reo Type": <>
          <BCFormInput 
            labelClass="gmr-background-label"
            type="dropdown"
            placeholder="Please Select"
            field="reotypeId"
            fluid={false}
            value={handleDisplayValue("reotypeId", reoschedule, reoSchedulePatchArray)}
            dropdownOptions={getDropdownOptions(props.applicationMetadata.reoTypes, ["id"], "name")}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field)}
            disabled={!props.isEditMode}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-reotypeId`} />
        </>,
        "Institution": <>
          <BCFormInput
            labelClass="gmr-background-label"
            placeholder="Construction Loan Services II"
            type="input"
            fieldType={GlobalConstants.FormInputTypes.INPUT}
            inputType={GlobalConstants.FormInputTypes.INPUT}
            value={handleDisplayValue("mortgageHolder1", reoschedule, reoSchedulePatchArray)}
            field="mortgageHolder1"
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-mortgageHolder1`} />
        </>,
        "Total Mortgage Amount": <>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="10000"
            value={handleDisplayValue("mortgageAmount1", reoschedule, reoSchedulePatchArray) || 0}
            field="mortgageAmount1"
            fieldType={GlobalConstants.InputTypes.DOLLAR}
            inputType={GlobalConstants.InputTypes.DOLLAR}
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field, GlobalConstants.InputTypes.DOLLAR)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-mortgageAmount1`} />
        </>,
        "Mortgage Status": <React.Fragment>
          <BCFormInput
            inputDropdownClass="guarantor-reo-status"
            labelClass="gmr-background-label"
            type="dropdown"
            placeholder="Please Select"
            value={handleDisplayValue("reostatusId", reoschedule, reoSchedulePatchArray)}
            field="reostatusId"
            dropdownOptions={getDropdownOptions(props.applicationMetadata.reoStatuses, ["id"], "name")}
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-reostatusId`} />
        </React.Fragment>,
        "Address": <React.Fragment>
          <BCFormInput
            type={GlobalConstants.FormInputTypes.PLACES}
            placeDetailsObject={reoschedule}
            disabled={!props.isEditMode}
            setPlaceDetailsObject={(placeDetailsObject) => handleAddressChange(placeDetailsObject, reoschedule)}
            placeholder="1234 Main Street, Seattle, WA 98052, USA"
            defaultValue={handleAddressValue({
              address: reoschedule.street,
              city: reoschedule.cityName,
              state: reoschedule.stateName,
              zip: reoschedule.zipCode
            })}
            placeFields={["address", "city", "state", "county", "zip"]}
            placeFieldsNameOverrides={[
              {
                addressComponent: "address",
                field: "street"
              },
              {
                addressComponent: "city",
                field: "cityName"
              },
              {
                addressComponent: "state",
                field: "stateName"
              },
              {
                addressComponent: "county",
                field: "countyName"
              },
              {
                addressComponent: "zip",
                field: "zipCode"
              },
            ]}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-street`} propText="Street name is missing" />
          {!props.invalidProps.includes(`reo-${reoschedule.id}-street`) && 
            <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-cityName`} propText="City name is missing"/>}
          {(!props.invalidProps.includes(`reo-${reoschedule.id}-street`) && !props.invalidProps.includes(`reo-${reoschedule.id}-cityName`)) && 
            <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-stateName`} propText="State name is missing"/>}
        </React.Fragment>,
        "Market Value": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="10000"
            value={handleDisplayValue("marketValue", reoschedule, reoSchedulePatchArray)}
            field="marketValue"
            fieldType={GlobalConstants.InputTypes.DOLLAR}
            inputType={GlobalConstants.InputTypes.DOLLAR}
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field, GlobalConstants.InputTypes.DOLLAR)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-marketValue`} />
        </React.Fragment>,
         "Parcel Number": <React.Fragment>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            placeholder="1"
            value={handleDisplayValue("parcelNumber", reoschedule, reoSchedulePatchArray)}
            field="parcelNumber"
            fieldType={GlobalConstants.InputTypes.STRING}
            inputType={GlobalConstants.InputTypes.STRING}
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-parcelNumber`} />
        </React.Fragment>,
        "Include as Additional Collateral?": <>
          <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            obj={reoschedule}
            inputKey={reoschedule.id}
            value={handleDisplayValue("additionalCollateral", reoschedule, reoSchedulePatchArray)}
            field="additionalCollateral"
            disabled={!props.isEditMode}
            handleFormChange={(value, field) => handleReoChange(reoschedule, value, field)}
          />
          <BCValidationMessage invalidProps={props.invalidProps} prop={`reo-${reoschedule.id}-additionalCollateral`} />
        </>,
      });
    });

    return formattedREO;
  };

  return (
    <React.Fragment>
      <BPLoanDetailSection
        title={props.assetTitle}
        accordionId={props.assetAccordionId}
        mainPatchObj={props.mainPatchObj}
        setMainPatchObj={props.setMainPatchObj}
        subPatchObjName="alschedule"
        subPatchObj={alSchedulePatchArray}
        setSubPatchObj={setAlSchedulePatchArray}
        isEditMode={props.isEditMode}
        handleAdd={() => handleALAdd(BrokerConstants.ALScheduleType.ASSET)}
        {...props.accordionProps}
        table={formatAL(BrokerConstants.ALScheduleType.ASSET).length > 0 && <Grid>
          <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BCTable
                dataRules={[{ field: "id", rule: "hide" }, { field: "Category", rule: "hide" }]}
                data={formatAL(BrokerConstants.ALScheduleType.ASSET)}
                thin
                dataSingleLine
                overflowX
                unstackable
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                editMode={props.isEditMode}
                actions={["add", "delete"]}
                isDeleteHide={!props.isEditMode}
                handleTableAdd={() => handleALAdd(BrokerConstants.ALScheduleType.ASSET)}
                handleTableDeleteById={handleALDelete}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
      <BPLoanDetailSection
        title={props.liabilitiesTitle}
        accordionId={props.liabilitiesAccordionId}
        mainPatchObj={props.mainPatchObj}
        setMainPatchObj={props.setMainPatchObj}
        subPatchObjName="alschedule"
        subPatchObj={alSchedulePatchArray}
        setSubPatchObj={setAlSchedulePatchArray}
        isEditMode={props.isEditMode}
        handleAdd={() => handleALAdd(BrokerConstants.ALScheduleType.LIABILITY)}
        {...props.accordionProps}
        table={formatAL(BrokerConstants.ALScheduleType.LIABILITY).length > 0 && <Grid>
          <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BCTable
                dataRules={[{ field: "id", rule: "hide" }, { field: "Category", rule: "hide" }]}
                data={formatAL(BrokerConstants.ALScheduleType.LIABILITY)}
                thin
                dataSingleLine
                overflowX
                unstackable
                editMode={props.isEditMode}
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                actions={["add", "delete"]}
                isDeleteHide={!props.isEditMode}
                handleTableAdd={() => handleALAdd(BrokerConstants.ALScheduleType.LIABILITY)}
                handleTableDeleteById={handleALDelete}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
      <BPLoanDetailSection
        title={props.reoTitle}
        accordionId={props.reoAccordionId}
        mainPatchObj={props.mainPatchObj}
        setMainPatchObj={props.setMainPatchObj}
        subPatchObjName="reoSchedule"
        subPatchObj={reoSchedulePatchArray}
        setSubPatchObj={setReoSchedulePatchArray}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        handleAdd={() => handleREOAdd()}
        table={formatREO().length > 0 && <Grid>
          <Grid.Row className="details-row">
            <Grid.Column width={16}>
              <BCTable
                data={formatREO()}
                dataRules={[{ field: "id", rule: "hide" }]}
                thin
                dataSingleLine
                overflowX
                unstackable
                editMode={props.isEditMode}
                brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
                actions={["add", "delete"]}
                isDeleteHide={!props.isEditMode}
                handleTableAdd={() => handleREOAdd()}
                handleTableDeleteById={handleREODelete}
              />
              <ReoValidationMessageBundle />
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
    </React.Fragment>
  );
};

export default BPLoanALReoTables;